import { createBrowserRouter, redirect } from "react-router";
import { RootLayout } from "./layouts/RootLayout";
import { AuthLayout } from "./layouts/AuthLayout";
import { AppLayout } from "./layouts/AppLayout";
import { RootErrorBoundary, RouteErrorBoundary } from "./components/ErrorBoundary";
import { protectedLoader } from "../lib/loaders";

// Auth routes
import { LoginPage } from "./routes/auth/login";
import { loginAction } from "./routes/auth/login.action";
import { RegisterPage } from "./routes/auth/register";
import { registerAction } from "./routes/auth/register.action";

// Protected routes
import { DashboardPage, loader as dashboardLoader } from "./routes/dashboard";
import DiscoverPage from "./routes/discover";
import MatchesPage from "./routes/matches";
import SavedPage from "./routes/saved";
import ActivityPage from "./routes/activity";
import AnalyticsPage from "./routes/analytics";

// Jobs routes
import { JobSearchPage, loader as jobSearchLoader } from "./routes/jobs/search";
import { JobDetailPage } from "./routes/jobs/[id]";
import { JobMatchesPage, loader as jobMatchesLoader } from "./routes/jobs/matches";
import { SavedJobsPage } from "./routes/jobs/saved";
import { RejectedJobsPage } from "./routes/jobs/rejected";

// Applications routes
import { ApplicationsPage, loader as applicationsLoader } from "./routes/applications/index";
import { ApplicationDetailPage, loader as applicationDetailLoader, action as applicationAction } from "./routes/applications/[id]";

// Resume routes
import { ResumesPage, loader as resumesLoader, action as resumesAction } from "./routes/resumes/index";
import { ResumeEditorPage, loader as resumeEditorLoader, action as resumeEditorAction } from "./routes/resumes/[id]";

// Strategy routes
import StrategyHomePage from "./routes/strategy/index";
import { ObjectivesPage } from "./routes/strategy/objectives";
import { StrategyLabPage } from "./routes/strategy/lab";

// Auto-Apply routes
import { AutoApplyPage, loader as autoApplyLoader } from "./routes/auto-apply/index";
import { CreateAutoApplyRulePage } from "./routes/auto-apply/create";

import { NotFoundPage } from "./routes/404";

// Onboarding routes
import { OnboardingResumePage, loader as onboardingResumeLoader } from "./routes/onboarding/resume";

// Settings routes
import { SettingsIndexPage, loader as settingsIndexLoader } from "./routes/settings/index";
import { SettingsProfilePage, loader as settingsProfileLoader, action as settingsProfileAction } from "./routes/settings/profile";
import { SettingsAccountPage, loader as settingsAccountLoader, action as settingsAccountAction } from "./routes/settings/account";
import { SettingsSecurityPage, loader as settingsSecurityLoader, action as settingsSecurityAction } from "./routes/settings/security";
import { SettingsNotificationsPage, loader as settingsNotificationsLoader, action as settingsNotificationsAction } from "./routes/settings/notifications";
import { SettingsConnectionsPage, loader as settingsConnectionsLoader, action as settingsConnectionsAction } from "./routes/settings/connections";



export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      // Public auth routes (no loader, prevent revalidation after successful POST)
      {
        path: "login",
        element: <AuthLayout><LoginPage /></AuthLayout>,
        action: loginAction,
        errorElement: <RouteErrorBoundary />,
        shouldRevalidate: () => false,
      },
      {
        path: "register", 
        element: <AuthLayout><RegisterPage /></AuthLayout>,
        action: registerAction,
        errorElement: <RouteErrorBoundary />,
        shouldRevalidate: () => false,
      },
      // Simplified onboarding: just optional resume upload
      {
        path: "onboarding",
        element: <OnboardingResumePage />,
        loader: onboardingResumeLoader,
        errorElement: <RouteErrorBoundary />,
        shouldRevalidate: () => false,
      },
      // Protected app shell - guards all children via protectedLoader
      {
        path: "/",
        element: <AppLayout />,
        loader: protectedLoader,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            loader: () => redirect('/dashboard'),
          },
          {
            path: "dashboard",
            element: <DashboardPage />,
            loader: dashboardLoader,
          },
          {
            path: "discover",
            element: <DiscoverPage />,
            loader: protectedLoader,
          },
          {
            path: "matches",
            element: <MatchesPage />,
            loader: protectedLoader,
          },
          {
            path: "saved",
            element: <SavedPage />,
            loader: protectedLoader,
          },
          // Strategy routes
          {
            path: "strategy",
            element: <StrategyHomePage />,
          },
          {
            path: "strategy/objectives",
            element: <ObjectivesPage />,
          },
          {
            path: "strategy/lab",
            element: <StrategyLabPage />,
          },
          // Jobs routes
          {
            path: "jobs",
            loader: () => redirect("/search-jobs"),
          },
          {
            path: "search-jobs",
            element: <JobSearchPage />,
            loader: jobSearchLoader,
          },
          {
            path: "jobs/search",
            element: <JobSearchPage />,
            loader: jobSearchLoader,
          },
          {
            path: "jobs/matches",
            element: <JobMatchesPage />,
            loader: jobMatchesLoader,
          },
          {
            path: "jobs/saved",
            element: <SavedJobsPage />,
            loader: protectedLoader,
          },
          {
            path: "jobs/rejected",
            element: <RejectedJobsPage />,
            loader: protectedLoader,
          },
          {
            path: "jobs/:id",
            element: <JobDetailPage />,
            loader: protectedLoader,
          },
          // Applications routes
          {
            path: "applications",
            element: <ApplicationsPage />,
            loader: applicationsLoader,
          },
          {
            path: "applications/:id",
            element: <ApplicationDetailPage />,
            loader: applicationDetailLoader,
            action: applicationAction,
          },
          // Resume routes
          {
            path: "resumes",
            element: <ResumesPage />,
            loader: resumesLoader,
            action: resumesAction,
          },
          {
            path: "resumes/:id",
            element: <ResumeEditorPage />,
            loader: resumeEditorLoader,
            action: resumeEditorAction,
          },
          // Auto-Apply routes
          {
            path: "auto-apply",
            element: <AutoApplyPage />,
            loader: autoApplyLoader,
          },
          {
            path: "auto-apply/create",
            element: <CreateAutoApplyRulePage />,
            loader: protectedLoader,
          },
          // Activity & Analytics routes
          {
            path: "activity",
            element: <ActivityPage />,
            loader: protectedLoader,
          },
          {
            path: "analytics",
            element: <AnalyticsPage />,
            loader: protectedLoader,
          },
          // Settings routes
          {
            path: "settings",
            element: <SettingsIndexPage />,
            loader: settingsIndexLoader,
          },
          {
            path: "settings/profile",
            element: <SettingsProfilePage />,
            loader: settingsProfileLoader,
            action: settingsProfileAction,
          },
          {
            path: "settings/account",
            element: <SettingsAccountPage />,
            loader: settingsAccountLoader,
            action: settingsAccountAction,
          },
          {
            path: "settings/security",
            element: <SettingsSecurityPage />,
            loader: settingsSecurityLoader,
            action: settingsSecurityAction,
          },
          {
            path: "settings/notifications",
            element: <SettingsNotificationsPage />,
            loader: settingsNotificationsLoader,
            action: settingsNotificationsAction,
          },
          {
            path: "settings/connections",
            element: <SettingsConnectionsPage />,
            loader: settingsConnectionsLoader,
            action: settingsConnectionsAction,
          },
        ],
      },
      {
        path: "logout",
        loader: async () => {
          // Let the backend handle logout and cookie clearing
          try {
            await fetch('https://api.nduka.io/api/v2/auth/logout', {
              method: 'POST',
              credentials: 'include',
            });
          } catch (error) {
            // Continue with logout even if API call fails
            console.error('Logout API error:', error);
          }
          return redirect("/login");
        },
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);